import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, HeartPulse, Leaf, Users, Target, Zap, ExternalLink, Globe, Sparkles } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';

const About = () => {
  usePageMeta({
    title: 'About | Talent Collective',
    description: 'Learn about ACT Grants x The Product Folks Talent Collective - connecting exceptional professionals with mission-driven organizations across India.'
  });

  const verticals = [
    {
      icon: GraduationCap,
      title: 'ACT For Education',
      description: 'Catalyzing quality and affordable ed-tech for India at scale. Supporting solutions that help underserved children and youth learn effectively.',
      stat: '18+ Organizations',
      bgColor: 'bg-sky-50',
      textColor: 'text-sky-600',
      borderColor: 'border-sky-200',
      link: 'https://actgrants.in/act-for-education/'
    },
    {
      icon: HeartPulse,
      title: 'ACT For Health',
      description: 'Improving access to quality and affordable healthcare in primary care, diabetes, cancer, tuberculosis and mental health.',
      stat: '24+ Organizations',
      bgColor: 'bg-rose-50',
      textColor: 'text-rose-600',
      borderColor: 'border-rose-200',
      link: 'https://actgrants.in/act-for-health/'
    },
    {
      icon: Leaf,
      title: 'ACT For Environment',
      description: 'Helping India accelerate decarbonisation and ensure water security through tech-first climate innovations.',
      stat: '19+ Organizations',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      borderColor: 'border-emerald-200',
      link: 'https://actgrants.in/act-for-environment/'
    },
    {
      icon: Users,
      title: "ACT For Women's Empowerment",
      description: "Accelerating India's progress on women's workforce participation through data, tech-led platforms and collaboration.",
      stat: '3+ Organizations',
      bgColor: 'bg-violet-50',
      textColor: 'text-violet-600',
      borderColor: 'border-violet-200',
      link: 'https://actgrants.in/act-for-women/'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="about-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white py-20">
        <div className="absolute top-0 left-0 right-0 h-1 bg-teal-600" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-teal-100/30 rounded-full blur-3xl" />
        
        <div className="container-custom relative">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 text-teal-700 text-sm font-semibold mb-6 border border-teal-200/50">
              About This Initiative
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight mb-6">
              ACT Grants x The Product Folks
              <span className="block text-teal-700">
                Talent Collective
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Connecting exceptional professionals with mission-driven organizations 
              building tech-first solutions for India's most pressing challenges.
            </p>
          </div>
        </div>
      </section>

      {/* About ACT Grants */}
      <section className="section-padding bg-white" data-testid="about-act-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-4">
                Our Story
              </span>
              <h2 className="heading-2 mb-6">About ACT Grants</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                ACT (Action Covid-19 Team) started as India's largest collective action to address 
                the immediate challenges of the COVID-19 pandemic. Today, ACT Grants focuses on 
                deploying catalytic funding to accelerate tech-first innovations creating social impact.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                With over 350 Crores deployed across 64+ portfolio organizations, ACT Grants supports 
                innovations in education, healthcare, environment, and women's workforce participation.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '350Cr+', label: 'Grants Deployed', icon: Sparkles },
                  { value: '64+', label: 'Portfolio Orgs', icon: Users },
                  { value: '1Bn+', label: 'Lives Impacted', icon: Globe },
                  { value: '100+', label: 'Funders', icon: Users }
                ].map((stat, i) => (
                  <div key={i} className="p-4 rounded-xl bg-teal-50 border border-teal-100">
                    <stat.icon className="h-5 w-5 text-teal-600 mb-2" />
                    <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square rounded-3xl bg-teal-50 p-8">
                <div className="h-full w-full rounded-2xl bg-white shadow-xl flex items-center justify-center">
                  <div className="text-center p-8">
                    <img 
                      src="https://actgrants.in/wp-content/themes/act/images/logo.png" 
                      alt="ACT Grants" 
                      className="h-16 mx-auto mb-6"
                    />
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">ACT Capital Foundation</h3>
                    <p className="text-gray-500 mb-6">For Social Impact</p>
                    <a href="https://actgrants.in" target="_blank" rel="noopener noreferrer">
                      <Button className="rounded-full bg-teal-700 hover:bg-teal-800 text-white shadow-lg shadow-teal-700/20">
                        Visit ACT Grants
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -right-4 px-5 py-3 rounded-full bg-white shadow-xl border border-teal-100">
                <span className="text-gray-800 font-semibold flex items-center gap-2">
                  <Globe className="h-4 w-4 text-teal-600" />
                  Building for India
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Four Verticals */}
      <section className="section-padding bg-gray-50" data-testid="verticals-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold mb-4">
              Focus Areas
            </span>
            <h2 className="heading-2 mb-4">Four Impact Verticals</h2>
            <p className="text-base md:text-lg text-gray-600">
              ACT Grants deploys catalytic funding across four key areas addressing India's most pressing challenges.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {verticals.map((vertical, i) => (
              <Card key={i} className={`p-8 bg-white border-2 ${vertical.borderColor} hover:border-teal-300 hover:shadow-xl hover:shadow-teal-100/50 transition-all duration-300 rounded-2xl`}>
                <div className="flex items-start gap-5">
                  <div className={`h-16 w-16 rounded-2xl ${vertical.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <vertical.icon className={`h-8 w-8 ${vertical.textColor}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-800">{vertical.title}</h3>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${vertical.bgColor} ${vertical.textColor}`}>
                        {vertical.stat}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 leading-relaxed">{vertical.description}</p>
                    <a 
                      href={vertical.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 group"
                    >
                      Learn more
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About This Platform */}
      <section className="section-padding bg-white" data-testid="platform-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-4">
                The Platform
              </span>
              <h2 className="heading-2 mb-4">About Talent Collective</h2>
            </div>
            
            <div className="space-y-8">
              <div className="flex gap-5 p-6 rounded-2xl bg-teal-50 border border-teal-100">
                <div className="h-14 w-14 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Target className="h-7 w-7 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-2">Our Mission</h3>
                  <p className="text-gray-600 leading-relaxed">
                    To connect exceptional professionals with ACT portfolio organizations, 
                    enabling mission-driven teams to access the talent they need to scale their impact.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-5 p-6 rounded-2xl bg-emerald-50 border border-emerald-100">
                <div className="h-14 w-14 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Users className="h-7 w-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-2">For Professionals</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Find meaningful work with organizations creating real impact. Whether you're a product manager, 
                    designer, or engineer, your skills can help solve India's most pressing challenges.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-5 p-6 rounded-2xl bg-blue-50 border border-blue-100">
                <div className="h-14 w-14 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Zap className="h-7 w-7 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-2">For Organizations</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Access a curated network of professionals who are passionate about social impact. 
                    Find fractional talent to help you scale your mission without the overhead of full-time hiring.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-teal-800 text-white relative overflow-hidden">
        <div className="container-custom relative text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to make an impact?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            Join the collective of professionals building for India's future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button className="h-14 px-10 bg-white text-teal-800 hover:bg-gray-100 rounded-full shadow-lg transition-all duration-300 hover:scale-105" data-testid="about-cta">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/projects">
              <Button variant="outline" className="h-14 px-10 text-white border-white/30 hover:bg-white/10 rounded-full transition-all duration-300">
                Browse Opportunities
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
